import { useEffect, useState, type ReactNode } from "react";
import { Flex, Stack } from "@/components/layout";
import { Text } from "@/components/typography";
import { Alert, Button, Dialog, Input, Select, Textarea } from "@/components/ui";
import { apiErrorMessage } from "@/api/client";
import { ApprovalPickerField } from "@/modules/approvals";
import { formatLabel, formatMoney } from "../labels";
import type { PayoutLinePreview } from "../types";

export function ReasonApprovalDialog({
  open,
  title,
  notice,
  actions,
  requireApproval = false,
  holdReason = false,
  onClose,
  onSubmit,
  busy = false,
  error = null,
}: {
  open: boolean;
  title: string;
  notice?: string;
  actions: string[];
  requireApproval?: boolean;
  holdReason?: boolean;
  onClose: () => void;
  onSubmit: (input: {
    action: string;
    reason?: string;
    approval_id?: string;
    hold_reason?: string;
  }) => Promise<void>;
  busy?: boolean;
  error?: string | null;
}) {
  const [action, setAction] = useState(actions[0] ?? "");
  const [reason, setReason] = useState("");
  const [approvalId, setApprovalId] = useState("");
  const [hold, setHold] = useState("");

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={title}
      actions={
        <>
          <Button variant="secondary" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button
            variant="primary"
            loading={busy}
            disabled={requireApproval && !approvalId.trim()}
            onClick={() =>
              void onSubmit({
                action,
                ...(reason.trim() ? { reason: reason.trim() } : {}),
                ...(approvalId.trim() ? { approval_id: approvalId.trim() } : {}),
                ...(hold.trim() ? { hold_reason: hold.trim() } : {}),
              })
            }
          >
            Confirm {formatLabel(action)}
          </Button>
        </>
      }
    >
      <Stack gap={16}>
        {notice ? (
          <Text role="small" color="secondary" as="p">
            {notice}
          </Text>
        ) : null}
        <Select
          label="Action"
          value={action}
          onChange={(event) => setAction(event.target.value)}
          options={actions.map((item) => ({
            value: item,
            label: formatLabel(item),
          }))}
        />
        <Textarea
          label="Reason (optional unless the API requires it)"
          value={reason}
          onChange={(event) => setReason(event.target.value)}
        />
        <ApprovalPickerField
          label={
            requireApproval
              ? "Approved Approval (required)"
              : "Approved Approval (when Dual/Super Admin required)"
          }
          value={approvalId}
          onChange={setApprovalId}
          required={requireApproval}
          minAuthority={requireApproval ? "dual" : undefined}
        />
        {holdReason ? (
          <Textarea
            label="Hold reason"
            value={hold}
            onChange={(event) => setHold(event.target.value)}
          />
        ) : null}
        {error ? <Alert tone="danger" title="Action failed">{error}</Alert> : null}
      </Stack>
    </Dialog>
  );
}

function FormDialog({
  open,
  title,
  notice,
  submitLabel,
  disabled,
  onClose,
  onSubmit,
  busy,
  error,
  children,
}: {
  open: boolean;
  title: string;
  notice?: string;
  submitLabel: string;
  disabled?: boolean;
  onClose: () => void;
  onSubmit: () => Promise<void>;
  busy: boolean;
  error: string | null;
  children: ReactNode;
}) {
  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={title}
      actions={
        <>
          <Button variant="secondary" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button
            variant="primary"
            loading={busy}
            disabled={disabled}
            onClick={() => void onSubmit()}
          >
            {submitLabel}
          </Button>
        </>
      }
    >
      <Stack gap={16}>
        {notice ? (
          <Text role="small" color="secondary" as="p">
            {notice}
          </Text>
        ) : null}
        {children}
        {error ? <Alert tone="danger" title="Request failed">{error}</Alert> : null}
      </Stack>
    </Dialog>
  );
}

export function CreateTransactionDialog({
  open,
  onClose,
  onSubmit,
  busy = false,
  error = null,
}: {
  open: boolean;
  onClose: () => void;
  onSubmit: (body: Record<string, unknown>) => Promise<void>;
  busy?: boolean;
  error?: string | null;
}) {
  const [transactionType, setTransactionType] = useState("booking_payment");
  const [amount, setAmount] = useState("");
  const [currency, setCurrency] = useState("USD");
  const [bookingId, setBookingId] = useState("");
  const [externalReference, setExternalReference] = useState("");

  return (
    <FormDialog
      open={open}
      title="Record Transaction"
      notice="This creates a money record. It does not change Booking status. Provider execution is out of scope."
      submitLabel="Create"
      busy={busy}
      error={error}
      disabled={!amount || !currency.trim()}
      onClose={onClose}
      onSubmit={() =>
        onSubmit({
          transaction_type: transactionType,
          amount: Number(amount),
          currency: currency.trim(),
          ...(bookingId.trim() ? { booking_id: bookingId.trim() } : {}),
          ...(externalReference.trim()
            ? { external_reference: externalReference.trim() }
            : {}),
        })
      }
    >
      <Select
        label="Type"
        value={transactionType}
        onChange={(event) => setTransactionType(event.target.value)}
        options={[
          "booking_payment",
          "payout",
          "refund",
          "adjustment",
          "fee",
          "commission",
          "settlement",
        ].map((item) => ({ value: item, label: formatLabel(item) }))}
      />
      <Input label="Amount" type="number" value={amount} onChange={(e) => setAmount(e.target.value)} />
      <Input label="Currency" value={currency} onChange={(e) => setCurrency(e.target.value)} />
      <Input label="Booking ID (optional)" value={bookingId} onChange={(e) => setBookingId(e.target.value)} />
      <Input
        label="External reference (optional)"
        value={externalReference}
        onChange={(e) => setExternalReference(e.target.value)}
      />
    </FormDialog>
  );
}

export function CreateAdjustmentDialog({
  open,
  onClose,
  onSubmit,
  busy = false,
  error = null,
}: {
  open: boolean;
  onClose: () => void;
  onSubmit: (body: Record<string, unknown>) => Promise<void>;
  busy?: boolean;
  error?: string | null;
}) {
  const [amount, setAmount] = useState("");
  const [currency, setCurrency] = useState("USD");
  const [reason, setReason] = useState("");
  const [bookingId, setBookingId] = useState("");

  return (
    <FormDialog
      open={open}
      title="Create adjustment Transaction"
      notice="Finalized amounts are never edited. An adjustment is a new Transaction."
      submitLabel="Create adjustment"
      busy={busy}
      error={error}
      disabled={!amount || !currency.trim()}
      onClose={onClose}
      onSubmit={() =>
        onSubmit({
          amount: Number(amount),
          currency: currency.trim(),
          ...(reason.trim() ? { reason: reason.trim() } : {}),
          ...(bookingId.trim() ? { booking_id: bookingId.trim() } : {}),
        })
      }
    >
      <Input label="Amount" type="number" value={amount} onChange={(e) => setAmount(e.target.value)} />
      <Input label="Currency" value={currency} onChange={(e) => setCurrency(e.target.value)} />
      <Input label="Booking ID (optional)" value={bookingId} onChange={(e) => setBookingId(e.target.value)} />
      <Textarea label="Reason" value={reason} onChange={(e) => setReason(e.target.value)} />
    </FormDialog>
  );
}

export function CreateRefundDialog({
  open,
  onClose,
  onSubmit,
  busy = false,
  error = null,
}: {
  open: boolean;
  onClose: () => void;
  onSubmit: (body: Record<string, unknown>) => Promise<void>;
  busy?: boolean;
  error?: string | null;
}) {
  const [transactionId, setTransactionId] = useState("");
  const [amount, setAmount] = useState("");
  const [reasonType, setReasonType] = useState("customer_request");
  const [reason, setReason] = useState("");
  const [bookingId, setBookingId] = useState("");

  return (
    <FormDialog
      open={open}
      title="Request Refund"
      notice="Requester, approver, and issuer must be three different people. Amounts of 1000 or more need Dual Approval. This does not change Booking status."
      submitLabel="Request"
      busy={busy}
      error={error}
      disabled={!transactionId.trim() || !amount || !reasonType.trim()}
      onClose={onClose}
      onSubmit={() =>
        onSubmit({
          transaction_id: transactionId.trim(),
          amount: Number(amount),
          reason_type: reasonType.trim(),
          ...(reason.trim() ? { reason: reason.trim() } : {}),
          ...(bookingId.trim() ? { booking_id: bookingId.trim() } : {}),
        })
      }
    >
      <Input
        label="Transaction ID"
        value={transactionId}
        onChange={(e) => setTransactionId(e.target.value)}
      />
      <Input label="Amount" type="number" value={amount} onChange={(e) => setAmount(e.target.value)} />
      <Input label="Reason type" value={reasonType} onChange={(e) => setReasonType(e.target.value)} />
      <Input label="Booking ID (optional)" value={bookingId} onChange={(e) => setBookingId(e.target.value)} />
      <Textarea label="Reason" value={reason} onChange={(e) => setReason(e.target.value)} />
    </FormDialog>
  );
}

export function CreatePayoutDialog({
  open,
  onClose,
  onSubmit,
  busy = false,
  error = null,
}: {
  open: boolean;
  onClose: () => void;
  onSubmit: (body: Record<string, unknown>) => Promise<void>;
  busy?: boolean;
  error?: string | null;
}) {
  const [payeeKind, setPayeeKind] = useState<"business" | "creator">("business");
  const [payeeId, setPayeeId] = useState("");
  const [amount, setAmount] = useState("");
  const [currency, setCurrency] = useState("USD");

  return (
    <FormDialog
      open={open}
      title="Create Payout"
      notice="A payout is to a Business or a Creator, never both. HQ records the payout; it does not send bank money."
      submitLabel="Create"
      busy={busy}
      error={error}
      disabled={!payeeId.trim() || !amount || !currency.trim()}
      onClose={onClose}
      onSubmit={() =>
        onSubmit({
          amount: Number(amount),
          currency: currency.trim(),
          ...(payeeKind === "business"
            ? { business_id: payeeId.trim() }
            : { creator_profile_id: payeeId.trim() }),
        })
      }
    >
      <Select
        label="Payee kind"
        value={payeeKind}
        onChange={(event) => setPayeeKind(event.target.value as "business" | "creator")}
        options={[
          { value: "business", label: "Business" },
          { value: "creator", label: "Creator" },
        ]}
      />
      <Input
        label={payeeKind === "business" ? "Business ID" : "Creator Profile ID"}
        value={payeeId}
        onChange={(e) => setPayeeId(e.target.value)}
      />
      <Input label="Amount" type="number" value={amount} onChange={(e) => setAmount(e.target.value)} />
      <Input label="Currency" value={currency} onChange={(e) => setCurrency(e.target.value)} />
    </FormDialog>
  );
}

export function AddLineItemDialog({
  open,
  payoutId,
  onClose,
  onPreview,
  onSubmit,
  busy = false,
  error = null,
}: {
  open: boolean;
  /** The payout being added to; a preview is only valid for this payout. */
  payoutId: string;
  onClose: () => void;
  /** The API's derivation for this payout + transaction, in plain words. */
  onPreview: (transactionId: string) => Promise<PayoutLinePreview>;
  onSubmit: (body: Record<string, unknown>) => Promise<void>;
  busy?: boolean;
  error?: string | null;
}) {
  const [transactionId, setTransactionId] = useState("");
  const [gross, setGross] = useState("");
  const [fee, setFee] = useState("0");
  const [commission, setCommission] = useState("0");
  const [net, setNet] = useState("");
  const [preview, setPreview] = useState<PayoutLinePreview | null>(null);
  const [previewing, setPreviewing] = useState(false);
  const [previewError, setPreviewError] = useState<string | null>(null);

  // A preview is money facts about one payout and one payment. Clear it on
  // open so a previous payout's preview can never be added here.
  useEffect(() => {
    if (!open) return;
    setTransactionId("");
    setGross("");
    setFee("0");
    setCommission("0");
    setNet("");
    setPreview(null);
    setPreviewing(false);
    setPreviewError(null);
  }, [open, payoutId]);

  /** The preview belongs to the payout currently open. */
  const previewHere = preview && preview.payout_id === payoutId ? preview : null;

  const clearPreview = () => {
    setPreview(null);
    setPreviewError(null);
  };

  const runPreview = async () => {
    const id = transactionId.trim();
    if (!id) return;
    setPreviewing(true);
    setPreviewError(null);
    try {
      setPreview(await onPreview(id));
    } catch (err) {
      setPreview(null);
      setPreviewError(apiErrorMessage(err));
    } finally {
      setPreviewing(false);
    }
  };

  return (
    <FormDialog
      open={open}
      title="Add payout line item"
      notice={
        previewHere
          ? "Adding records exactly the amounts below — the API works them out again from the payment."
          : "Work the line out from the payment first. Net must equal gross minus fee minus commission; the API is authoritative."
      }
      submitLabel="Add"
      busy={busy}
      error={error}
      disabled={
        previewHere
          ? !previewHere.can_add
          : !transactionId.trim() || !gross || !net || previewing
      }
      onClose={onClose}
      onSubmit={() =>
        previewHere
          ? onSubmit({ transaction_id: previewHere.transaction_id })
          : onSubmit({
              transaction_id: transactionId.trim(),
              gross_amount: Number(gross),
              fee_amount: Number(fee),
              commission_amount: Number(commission),
              net_amount: Number(net),
            })
      }
    >
      <Stack gap={8}>
        <Input
          label="Transaction ID"
          value={transactionId}
          onChange={(e) => {
            setTransactionId(e.target.value);
            clearPreview();
          }}
        />
        <Flex justify="flex-end">
          <Button
            size="sm"
            variant="secondary"
            loading={previewing}
            disabled={!transactionId.trim() || previewing}
            onClick={() => void runPreview()}
          >
            Preview
          </Button>
        </Flex>
      </Stack>
      {previewError ? (
        <Alert tone="danger" title="Could not preview this payment">
          {previewError}
        </Alert>
      ) : null}
      {previewHere ? (
        <>
          <Text role="small" color="secondary" as="p">
            {previewHere.line_for === "venue"
              ? "This payout is the venue's. The line is the venue's share of the gig's payout."
              : previewHere.line_for === "gig"
                ? "This payout is the gig's. The venue's share is deducted from this line."
                : "This payment carries no venue share."}
          </Text>
          {previewHere.summary ? <Text as="p">{previewHere.summary}</Text> : null}
          <Text role="small" color="secondary" as="p">
            Gross {formatMoney(previewHere.line.gross_amount, previewHere.currency)} · fee{" "}
            {formatMoney(previewHere.line.fee_amount, previewHere.currency)} · commission{" "}
            {formatMoney(previewHere.line.commission_amount, previewHere.currency)} · net{" "}
            {formatMoney(previewHere.line.net_amount, previewHere.currency)}
          </Text>
          {previewHere.split ? (
            <Text role="small" color="secondary" as="p">
              {previewHere.split.percent}% of the{" "}
              {formatMoney(previewHere.split.gig_payout_amount, previewHere.currency)} gig payout
              (HUDDL {formatMoney(previewHere.split.huddl_amount, previewHere.currency)} of the{" "}
              {formatMoney(previewHere.split.charged_amount, previewHere.currency)} charged). Venue
              business {previewHere.split.venue_business_id}.
            </Text>
          ) : null}
          {!previewHere.can_add ? (
            <Alert tone="danger" title="This payout is closed">
              A line cannot be added to a payout in this status.
            </Alert>
          ) : null}
          <Flex justify="flex-end">
            <Button size="sm" variant="secondary" onClick={clearPreview}>
              Enter amounts by hand
            </Button>
          </Flex>
        </>
      ) : (
        <>
          <Input label="Gross" type="number" value={gross} onChange={(e) => setGross(e.target.value)} />
          <Input label="Fee" type="number" value={fee} onChange={(e) => setFee(e.target.value)} />
          <Input
            label="Commission"
            type="number"
            value={commission}
            onChange={(e) => setCommission(e.target.value)}
          />
          <Input label="Net" type="number" value={net} onChange={(e) => setNet(e.target.value)} />
        </>
      )}
    </FormDialog>
  );
}

export function CreateSettlementDialog({
  open,
  onClose,
  onSubmit,
  busy = false,
  error = null,
}: {
  open: boolean;
  onClose: () => void;
  onSubmit: (body: Record<string, unknown>) => Promise<void>;
  busy?: boolean;
  error?: string | null;
}) {
  const [payoutId, setPayoutId] = useState("");
  const [expected, setExpected] = useState("");
  const [reference, setReference] = useState("");

  return (
    <FormDialog
      open={open}
      title="Create Settlement"
      submitLabel="Create"
      busy={busy}
      error={error}
      disabled={!payoutId.trim() || !expected}
      onClose={onClose}
      onSubmit={() =>
        onSubmit({
          payout_id: payoutId.trim(),
          expected_amount: Number(expected),
          ...(reference.trim() ? { settlement_reference: reference.trim() } : {}),
        })
      }
    >
      <Input label="Payout ID" value={payoutId} onChange={(e) => setPayoutId(e.target.value)} />
      <Input
        label="Expected amount"
        type="number"
        value={expected}
        onChange={(e) => setExpected(e.target.value)}
      />
      <Input
        label="Settlement reference (optional)"
        value={reference}
        onChange={(e) => setReference(e.target.value)}
      />
    </FormDialog>
  );
}

export function CreateCommissionDialog({
  open,
  onClose,
  onSubmit,
  busy = false,
  error = null,
}: {
  open: boolean;
  onClose: () => void;
  onSubmit: (body: Record<string, unknown>) => Promise<void>;
  busy?: boolean;
  error?: string | null;
}) {
  const [scopeType, setScopeType] = useState("market");
  const [scopeId, setScopeId] = useState("");

  return (
    <FormDialog
      open={open}
      title="Create Commission Rule"
      notice="Activating a rule requires Super Admin approval and replaces the prior active rule."
      submitLabel="Create draft"
      busy={busy}
      error={error}
      disabled={!scopeType.trim() || !scopeId.trim()}
      onClose={onClose}
      onSubmit={() =>
        onSubmit({
          rule_scope_type: scopeType.trim(),
          rule_scope_id: scopeId.trim(),
        })
      }
    >
      <Input
        label="Scope type"
        value={scopeType}
        onChange={(e) => setScopeType(e.target.value)}
      />
      <Input label="Scope ID" value={scopeId} onChange={(e) => setScopeId(e.target.value)} />
    </FormDialog>
  );
}

export function CreateExceptionDialog({
  open,
  onClose,
  onSubmit,
  busy = false,
  error = null,
}: {
  open: boolean;
  onClose: () => void;
  onSubmit: (body: Record<string, unknown>) => Promise<void>;
  busy?: boolean;
  error?: string | null;
}) {
  const [exceptionType, setExceptionType] = useState("failed_payment");
  const [severity, setSeverity] = useState("normal");
  const [transactionId, setTransactionId] = useState("");

  return (
    <FormDialog
      open={open}
      title="Open Financial Exception"
      submitLabel="Create"
      busy={busy}
      error={error}
      onClose={onClose}
      onSubmit={() =>
        onSubmit({
          exception_type: exceptionType,
          severity,
          ...(transactionId.trim() ? { transaction_id: transactionId.trim() } : {}),
        })
      }
    >
      <Select
        label="Type"
        value={exceptionType}
        onChange={(e) => setExceptionType(e.target.value)}
        options={[
          "failed_payment",
          "duplicate_payment_signal",
          "unmatched_settlement",
          "delayed_payout",
          "chargeback",
          "dispute",
          "commission_anomaly",
          "creator_payment_exception",
        ].map((item) => ({ value: item, label: formatLabel(item) }))}
      />
      <Select
        label="Severity"
        value={severity}
        onChange={(e) => setSeverity(e.target.value)}
        options={["low", "normal", "high", "urgent", "critical"].map((item) => ({
          value: item,
          label: formatLabel(item),
        }))}
      />
      <Input
        label="Transaction ID (optional)"
        value={transactionId}
        onChange={(e) => setTransactionId(e.target.value)}
      />
    </FormDialog>
  );
}

export function CreateDisputeDialog({
  open,
  onClose,
  onSubmit,
  busy = false,
  error = null,
}: {
  open: boolean;
  onClose: () => void;
  onSubmit: (body: Record<string, unknown>) => Promise<void>;
  busy?: boolean;
  error?: string | null;
}) {
  const [reason, setReason] = useState("");
  const [transactionId, setTransactionId] = useState("");
  const [refundId, setRefundId] = useState("");

  return (
    <FormDialog
      open={open}
      title="Open Financial Dispute"
      notice="Financial Disputes stay separate from Financial Exceptions."
      submitLabel="Open"
      busy={busy}
      error={error}
      disabled={reason.trim().length < 3}
      onClose={onClose}
      onSubmit={() =>
        onSubmit({
          reason: reason.trim(),
          ...(transactionId.trim() ? { transaction_id: transactionId.trim() } : {}),
          ...(refundId.trim() ? { refund_id: refundId.trim() } : {}),
        })
      }
    >
      <Textarea label="Reason" value={reason} onChange={(e) => setReason(e.target.value)} />
      <Input
        label="Transaction ID (optional)"
        value={transactionId}
        onChange={(e) => setTransactionId(e.target.value)}
      />
      <Input
        label="Refund ID (optional)"
        value={refundId}
        onChange={(e) => setRefundId(e.target.value)}
      />
    </FormDialog>
  );
}
